<template>
  <div>
    <CModal
      :title="title"
      :color="color"
      :show.sync="importCustomerModal"
      class="basic-modal-color import-guests-modal"
      size="xl"
    >
      <CForm>
        <ImportTypeSelector v-model="form.import_type" @input="onImportTypeChanged" />

        <ImportFileInput
          :file="selectedFile"
          :error="fileError"
          :max-size-mb="maxFileSizeMb"
          @file-change="onFileChange"
          @clear-file="clearFile"
        />

        <ImportInstructions :import-type="form.import_type" />
        <ImportExampleTable :import-type="form.import_type" :open-token="exampleOpenToken" />

        <ImportValidationSummary v-if="validationSummary" :summary="validationSummary" />
        <ImportValidationLog v-if="validationItems.length > 0" :items="validationItems" />
      </CForm>

      <template #footer>
        <CButton @click="closeModal" color="secondary">Cerrar</CButton>
        <CButton @click="downloadTemplate" color="success">Descargar plantilla</CButton>
        <CButton @click="validateFile" color="info" :disabled="!canValidate || isValidating">
          {{ isValidating ? 'Validando...' : 'Validar archivo' }}
        </CButton>
        <CButton @click="openConfirm" color="primary" :disabled="!canImport || isImporting">
          {{ isImporting ? 'Importando...' : 'Importar invitados' }}
        </CButton>
      </template>
    </CModal>

    <ImportConfirmationModal
      :show.sync="showConfirmation"
      :import-type="form.import_type"
      :summary="validationSummary || {}"
      @back="showConfirmation = false"
      @confirm="importGuests"
    />
  </div>
</template>

<script>
import ws from '@/services/events_guests';
import store from '@/store';

import ImportTypeSelector from '@/components/events/event/detail/guests/import/ImportTypeSelector.vue';
import ImportFileInput from '@/components/events/event/detail/guests/import/ImportFileInput.vue';
import ImportInstructions from '@/components/events/event/detail/guests/import/ImportInstructions.vue';
import ImportExampleTable from '@/components/events/event/detail/guests/import/ImportExampleTable.vue';
import ImportValidationSummary from '@/components/events/event/detail/guests/import/ImportValidationSummary.vue';
import ImportValidationLog from '@/components/events/event/detail/guests/import/ImportValidationLog.vue';
import ImportConfirmationModal from '@/components/events/event/detail/guests/import/ImportConfirmationModal.vue';

export default {
  name: 'ImportGuestsModal',
  components: {
    ImportTypeSelector,
    ImportFileInput,
    ImportInstructions,
    ImportExampleTable,
    ImportValidationSummary,
    ImportValidationLog,
    ImportConfirmationModal,
  },
  data() {
    return {
      importCustomerModal: false,
      title: 'Importar invitados',
      color: 'info',
      form: this.getEmptyForm(),
      selectedFile: null,
      validationResult: null,
      validatedFingerprint: '',
      fileError: '',
      isValidating: false,
      isImporting: false,
      showConfirmation: false,
      exampleOpenToken: 0,
      maxFileSizeMb: Number(process.env.VUE_APP_IMPORT_MAX_FILE_MB || 10),
    }
  },
  computed: {
    validationSummary() {
      return this.validationResult && this.validationResult.summary ? this.validationResult.summary : null
    },
    validationItems() {
      return this.validationResult && this.validationResult.log ? this.validationResult.log : []
    },
    hasBlockingErrors() {
      return this.validationSummary ? Number(this.validationSummary.error_rows || 0) > 0 : true
    },
    canValidate() {
      return !!this.form.import_type && !!this.selectedFile && this.fileError === ''
    },
    canImport() {
      return (
        this.canValidate &&
        !!this.validationResult &&
        !this.hasBlockingErrors &&
        this.validatedFingerprint !== '' &&
        this.validatedFingerprint === this.currentFingerprint()
      )
    }
  },
  methods: {
    importExcelModal(event_id) {
      this.importCustomerModal = true
      this.resetState()
      this.form.event_id = event_id
      this.exampleOpenToken++
    },
    getEmptyForm() {
      return {
        event_id: 0,
        import_type: 'initial_load',
      }
    },
    resetState() {
      this.form = this.getEmptyForm()
      this.selectedFile = null
      this.fileError = ''
      this.validationResult = null
      this.validatedFingerprint = ''
      this.isValidating = false
      this.isImporting = false
      this.showConfirmation = false
    },
    closeModal() {
      this.importCustomerModal = false
      this.resetState()
    },
    onImportTypeChanged() {
      this.invalidateValidation()
    },
    onFileChange(file) {
      this.selectedFile = file
      this.fileError = this.validateClientFile(file)
      this.invalidateValidation()
    },
    clearFile() {
      this.selectedFile = null
      this.fileError = ''
      this.invalidateValidation()
    },
    validateClientFile(file) {
      if (!file) {
        return 'Debes seleccionar un archivo.'
      }

      const extension = (file.name || '').split('.').pop().toLowerCase()
      if (!['xls', 'xlsx'].includes(extension)) {
        return 'Formato inválido. Solo se permiten archivos .xls y .xlsx.'
      }

      if (!file.size || file.size <= 0) {
        return 'El archivo no puede estar vacío.'
      }

      const maxBytes = this.maxFileSizeMb * 1024 * 1024
      if (file.size > maxBytes) {
        return 'El archivo excede el tamaño máximo permitido.'
      }

      return ''
    },
    invalidateValidation() {
      this.validationResult = null
      this.validatedFingerprint = ''
      this.showConfirmation = false
    },
    currentFingerprint() {
      if (!this.selectedFile) {
        return ''
      }

      return [
        this.form.import_type,
        this.selectedFile.name,
        this.selectedFile.size,
        this.selectedFile.lastModified
      ].join('|')
    },
    buildFormData() {
      const formData = new FormData()
      formData.append('event_id', this.form.event_id)
      formData.append('import_type', this.form.import_type)
      if (this.selectedFile) {
        formData.append('document', this.selectedFile)
      }
      return formData
    },
    async validateFile() {
      if (!this.canValidate) {
        return
      }

      this.isValidating = true
      this.loading()

      try {
        const response = await ws.validateImport(this.buildFormData())
        if (response.type === 'success') {
          this.validationResult = response.data
          this.validatedFingerprint = this.currentFingerprint()
        }
        this.showToast(response.type, response.message)
      } catch (error) {
        this.showToast('error', 'No se pudo validar el archivo. Intenta nuevamente.')
      }

      this.isValidating = false
      this.loaded()
    },
    openConfirm() {
      if (!this.canImport) {
        return
      }
      this.showConfirmation = true
    },
    async importGuests() {
      if (!this.canImport) {
        return
      }

      this.showConfirmation = false
      this.isImporting = true
      this.loading()

      try {
        const response = await ws.import(this.buildFormData())

        if (response.type === 'success') {
          this.validationResult = response.data
          this.validatedFingerprint = this.currentFingerprint()

          const summary = response.data && response.data.summary ? response.data.summary : {}
          const withObservations = Number(summary.warning_rows || 0) > 0 || Number(summary.ignored_rows || 0) > 0

          if (withObservations) {
            this.showToast('warning', 'La importación finalizó con observaciones. Revisa el resumen para conocer los detalles.')
          } else {
            this.showToast('success', 'Los invitados fueron importados correctamente.')
          }

          this.$emit('refresh')
        } else {
          this.showToast(response.type, response.message)
        }
      } catch (error) {
        this.showToast('error', 'No se pudo completar la importación. Intenta nuevamente.')
      }

      this.isImporting = false
      this.loaded()
    },
    async downloadTemplate() {
      await ws.getTemplate(this.form.import_type)
    },
    showToast(type, message) {
      store.commit('toast', { color: type, message: message })
    },
    loading() {
      store.commit('loading')
    },
    loaded() {
      store.commit('loaded')
    }
  }
}
</script>
<style>
.basic-modal-color .modal-header {
  background-color: #668269 !important;
}

.import-guests-modal .modal-body {
  max-height: 70vh;
  overflow-y: auto;
}
</style>